<!-- **** -------------------------------------------------------------------------- SHARED SCRIPT -------------------------------------------------------------------------- **** -->
<script lang="ts">
    import { usePointer } from '@shared/utils/UseEvent';
    import { reactive, ref, onMounted, Ref } from 'vue';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'Slider',
    };
    export default {
        name: component.name,
    };
</script>

<!-- **** -------------------------------------------------------------------------- SCRIPT -------------------------------------------------------------------------- **** -->
<script lang="ts" setup>
    const props = withDefaults(
            defineProps<{
                min?: number;
                max?: number;
                value: number;
                step?: number;
                flip?: boolean;

                direction?: 'horizontal' | 'vertical';
                size?: number;
                unit?: string;
                contain?: boolean;

                stringValues?: string[];

                info?:
                    | {
                          size?: number;
                          pos?: 'start' | 'end';
                          show?: 'value' | 'all';
                      }
                    | 'hidden';
                handle?: {
                    size?: IntRange<1, 100>;
                    asp?: number;
                    pull?: number;
                };
                railSize?: IntRange<1, 100>;

                onValueChange?: Func;
            }>(),
            {
                min: 0,
                max: 100,
                step: 1,
                flip: false,

                direction: 'horizontal',
                size: 2,
                unit: 'rem',
                contain: true,

                railSize: 100,
            }
        ),
        info:
            | {
                  size?: number;
                  pos?: 'start' | 'end';
                  show?: 'value' | 'all';
              }
            | 'hidden' =
            props.info === 'hidden'
                ? props.info
                : {
                      size: 2,
                      pos: 'start',
                      show: 'all',
                      ...(typeof props.info === 'object' ? props.info : {}),
                  },
        handle = {
            size: 2,
            asp: 30,
            pull: 0.4,
            ...(props.handle || {}),
        },
        state = reactive({
            handleOffset: 0,
        }),
        emit = defineEmits<{
            (ev: 'update:value', value: number): void;
        }>();

    let usedStyles: {
        sliderSpan: 'offsetWidth' | 'offsetHeight';
        eventOffsetAxis: 'offsetX' | 'offsetY';
        axis: 'x' | 'y';
    } = {
        // defaults
        // direction === 'horizontal'
        sliderSpan: 'offsetWidth',
        eventOffsetAxis: 'offsetX',
        axis: 'x',
    };
    if (props.direction === 'vertical') {
        usedStyles = {
            sliderSpan: 'offsetHeight',
            eventOffsetAxis: 'offsetY',
            axis: 'y',
        };
    }

    const sliderRef: Ref<HTMLElement | null> = ref(null),
        range = props.max - props.min,
        min = props.flip ? props.max : props.min,
        max = props.flip ? props.min : props.max,
        transitionStep = Math.abs(range / props.step) < 40;

    onMounted(() => {
        let ppp = sliderRef.value![usedStyles.sliderSpan] / range;

        state.handleOffset = props.flip ? range * ppp - props.value * ppp : props.value * ppp;
    });

    const dragHandle = (ev: PointerEvent, startOffset: number = ~~ev[usedStyles.eventOffsetAxis]) => {
        let slider = sliderRef.value!,
            sliderSpan = slider[usedStyles.sliderSpan],
            pm = range / sliderSpan, // point modifier
            ppp = sliderSpan / range, // points per pixel
            pps = props.step * ppp; // points per step

        const setOffset = (val: number, pull: number = 0) => {
            let pointerOffset = startOffset + val,
                rem = pointerOffset % pps,
                remMod = ~~((((rem || 1) / pps) * 10) / 10),
                offset = pointerOffset - rem;

            if (rem >= pps / 2) offset += pps;

            if (pointerOffset > 0 && pointerOffset < sliderSpan) {
                state.handleOffset = transitionStep ? ~~(offset - (rem >= pps / 2 ? (pps - rem) * (pull * remMod) : rem * -(pull * (1 - remMod)))) : ~~offset;
                emit('update:value', props.flip ? range - ~~(offset * pm) : ~~(offset * pm));
            } else if (pointerOffset <= 0 && pointerOffset !== 0) {
                state.handleOffset = 0;
                emit('update:value', min);
            } else if (pointerOffset >= sliderSpan && pointerOffset !== sliderSpan) {
                state.handleOffset = sliderSpan;
                emit('update:value', max);
            }
        };

        setOffset(0, handle.pull);

        usePointer.move(({ offset }, ev) => {
            setOffset(offset[usedStyles.axis], handle.pull);
        }, ev);

        usePointer.up(({ offset }, ev) => {
            setOffset(offset[usedStyles.axis]);
        }, ev);
    };
</script>

<!-- **** -------------------------------------------------------------------------- TEMPLATE -------------------------------------------------------------------------- **** -->
<template>
    <div class="ugs-slider-container" :class="[direction, 'info-' + (info !== 'hidden' ? info.show : info)]">
        <div class="ugs-slider-info" v-if="info !== 'hidden'">
            <div class="info-min">{{ min }}</div>
            <div class="info-value">{{ value }}</div>
            <div class="info-max">{{ max }}</div>
        </div>
        <div class="ugs-slider" ref="sliderRef" :style="`--contain: ${contain ? 'var(--calc-handle-asp)' : 0}`" @pointerdown.left.stop.prevent="dragHandle">
            <slot name="rail">
                <span class="ugs-slider-rail" />
            </slot>
            <slot name="handle">
                <div class="ugs-handle-container">
                    <span
                        class="ugs-slider-handle"
                        :class="transitionStep && 'transition-step'"
                        :style="`--handle-offset: ${state.handleOffset}px`"
                        @pointerdown.left.stop.prevent="(ev) => dragHandle(ev, state.handleOffset)" />
                </div>
            </slot>
        </div>
    </div>
</template>

<!-- **** -------------------------------------------------------------------------- STYLES -------------------------------------------------------------------------- **** -->
<style>
    .ugs-slider-container {
        display: grid;
        position: relative;
        grid-template-rows: 1fr;
        grid-template-columns: 1fr;
    }
    .ugs-slider-container.horizontal:not(.info-hidden) {
        grid-template-rows: 1fr 1fr;
    }
    .ugs-slider-container.vertical:not(.info-hidden) {
        grid-template-columns: 1fr 1fr;
    }

    /* slider info */
    .ugs-slider-info {
        --info-size: 2.4rem;
        --info-margin: 0.4rem;

        display: flex;
        position: relative;
    }
    .ugs-slider-container.horizontal .ugs-slider-info {
        justify-content: center;
        align-items: flex-end;
        height: var(--info-size);
        margin-bottom: var(--info-margin);
    }
    .ugs-slider-container.vertical .ugs-slider-info {
        justify-content: flex-end;
        align-items: center;
        width: var(--info-size);
        margin-right: var(--info-margin);
    }

    .ugs-slider-info :where(.info-min, .info-max) {
        position: absolute;
        bottom: 0;
        font-size: 14px;
        line-height: 14px;
        color: rgb(79, 79, 79);
    }
    .ugs-slider-container.horizontal .info-min {
        left: 0;
    }
    .ugs-slider-container.horizontal .info-max {
        right: 0;
    }
    .ugs-slider-container.vertical .info-min {
        top: 0;
    }
    .ugs-slider-container.vertical .info-max {
        bottom: 0;
    }

    .ugs-slider-info .info-value {
        font-size: 18px;
        line-height: 18px;
        color: #000;
    }

    /* slider */
    .ugs-slider {
        --slider-size: 2rem;
        --handle-size: 0.8;
        --handle-asp: 0.4;
        --handle-b-radius: 2px;
        --calc-handle-size: calc(var(--slider-size) * var(--handle-size));
        --calc-handle-asp: calc(var(--slider-size) * (var(--handle-size) * var(--handle-asp)));
        --rail-size: 25%;
        --rail-b-radius: 2px;
        --rail-clr: rgb(92 103 143);
        --contain: 0;
        --contain-handle: 0;

        display: block;
        position: relative;
    }

    .ugs-slider-container.horizontal .ugs-slider {
        height: var(--slider-size);
        width: calc(100% - var(--contain));
        /* margin-left: calc(var(--contain) * .5); */
    }
    .ugs-slider-container.vertical .ugs-slider {
        height: calc(100% - var(--contain));
        width: var(--slider-size);
        /* margin-top: calc(var(--contain) * .5); */
    }

    /* slider rail */
    .ugs-slider-rail {
        display: block;
        position: absolute;
        margin: auto;
        border-radius: var(--rail-b-radius);

        background-color: var(--rail-clr);
    }

    .ugs-slider-container.horizontal .ugs-slider-rail {
        height: var(--rail-size);
        width: calc(100% + var(--contain));
        top: 0;
        bottom: 0;
        /* left: calc(var(--contain) * -.5); */
        /* transform: translate3d(calc(var(--contain) * -.5), 0, 0); */
    }
    .ugs-slider-container.vertical .ugs-slider-rail {
        height: calc(100% + var(--contain));
        width: var(--rail-size);
        /* top: calc(var(--contain) * -.5); */
        right: 0;
        left: 0;

        /* transform: translate3d(0, calc(var(--contain) * -.5), 0); */
    }
    .ugs-slider-container.horizontal {
        width: 100%;
    }
    .ugs-slider-container.vertical {
        height: 100%;
    }

    .ugs-slider-handle {
        --handle-offset: 0;

        display: block;
        position: absolute;

        margin: auto;
        border-radius: var(--handle-b-radius);

        outline: 1px solid black;
        background-color: #fff;
    }

    .ugs-slider-handle.transition-step {
        transition: transform 0.1s ease-out;
    }

    .ugs-slider-container.horizontal .ugs-slider-handle {
        height: var(--calc-handle-size);
        width: var(--calc-handle-asp);
        top: 0;
        bottom: 0;
        left: calc(var(--calc-handle-asp) / 2);

        transform: translate3d(calc(var(--handle-offset) - 50%), 0, 0);
    }
    .ugs-slider-container.vertical .ugs-slider-handle {
        height: var(--calc-handle-asp);
        width: var(--calc-handle-size);
        top: calc(var(--calc-handle-asp) / 2);
        right: 0;
        left: 0;

        transform: translate3d(0, calc(var(--handle-offset) - 50%), 0);
    }

    /*
   color picker
*/
    .ugs-slider-container.color-picker {
        margin-left: 0.3rem;
    }
    .ugs-slider-container.color-picker .ugs-slider {
        --slider-size: 1.4rem;
        --handle-size: 1;
        --handle-asp: 0.25;
        --rail-size: 100%;
        --rail-b-radius: 0;
    }
    .ugs-slider-container.color-picker .ugs-slider-rail {
        /* outline: 1px solid #c9a4c8; */

        overflow: hidden;
    }

    .ugs-slider-container.color-picker.alpha .ugs-slider-rail:after {
        content: '';
        display: block;
        position: absolute;
        height: 100%;
        width: 100%;
        top: 0;
        left: 0;

        background-image: linear-gradient(0, transparent 0%, hsl(var(--cp-hue), 100%, 50%, 100%) 100%);
    }
</style>
